import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
  Injector,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DateEx } from '../../../../functions/date-ex';
import {
  EmployeeValue,
  OperatorType,
} from '../../../../models/api/alerts/alert';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { IconButtonComponent } from '../../../core/buttons/icon-button.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { SliderComponent } from '../../../core/controls/slider.component';
import { YesNoComponent } from '../../../core/controls/yes-no.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import {
  scheduleFocusAtIndex,
  scheduleFocusLast,
  shouldScheduleFocusAfterAction,
} from '../../../../utilities/accessibility';
import { ValidatorsEx } from '../../../../validators/validators-ex';

@Component({
  selector: 'app-alert-delivery-step',
  imports: [
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    PageHeaderComponent,
    YesNoComponent,
    SliderComponent,
    CommonModule,
    RangeComponent,
    IconButtonComponent,
    RequiredFieldsNoteComponent,
    RadioButtonComponent,
  ],
  template: `
    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-pb-8">
      <app-page-header
        class="md:tw-col-span-2"
        label="Delivery and Scheduling"
        [level]="2"
        appearance="h3"
      >
        <ng-template #right>
          <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
        </ng-template>
      </app-page-header>

      <div
        role="group"
        aria-labelledby="delivery-title-desc"
        class="tw-contents"
      >
        <span class="label" id="delivery-title-desc">
          Enter a title for the alert
        </span>
        <app-input [control]="fg.controls.title" label="Title"></app-input>
      </div>
      <div
        role="group"
        aria-labelledby="delivery-daterange-desc"
        class="tw-contents"
      >
        <span class="label" id="delivery-daterange-desc">
          Provide the date range to start the alert
        </span>
        <app-range
          [control]="fg.controls.startDate"
          [toControl]="fg.controls.endDate"
          [compact]="false"
          [maxSpanDays]="365"
        ></app-range>
      </div>
      <div
        role="group"
        aria-labelledby="delivery-enable-desc"
        class="tw-contents"
      >
        <span class="label" id="delivery-enable-desc"> Enable alert </span>
        <app-yes-no
          class="delivery-inline-radio"
          [control]="fg.controls.enabled"
        />
      </div>

      <div
        role="group"
        aria-labelledby="delivery-frequency-desc"
        class="tw-contents"
      >
        <span class="label" id="delivery-frequency-desc">
          How often would you like to receive notifications?
        </span>

        <app-radio-button
          class="delivery-inline-radio"
          [options]="deliveryFrequency"
          optionLabel="description"
          optionValue="value"
          [control]="fg.controls.deliveryFrequency"
          aria-label="Delivery frequency"
          [vertical]="false"
          classes="tw-pt-2"
        />
      </div>

      <div
        role="group"
        aria-labelledby="delivery-method-desc"
        [attr.aria-required]="isDeliveryMethodControlRequired ? 'true' : null"
        class="tw-contents"
      >
        <span class="label" id="delivery-method-desc">
          How would you like to receive notifications?
          @if (isDeliveryMethodControlRequired) {
            <span class="tw-sr-only">, required</span>
          }
        </span>

        <div class="tw-flex tw-items-start tw-gap-2">
          <div
            class="tw-flex tw-min-w-0 tw-grow tw-flex-col tw-gap-2 tw-rounded tw-border tw-border-solid tw-border-neutral-300 tw-p-3"
          >
            <div class="tw-flex tw-items-center tw-gap-2">
              <input
                class="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-neutral-400 focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-[3px]"
                type="checkbox"
                id="alert-delivery-method-email"
                [checked]="hasDeliveryMethod('email')"
                [attr.aria-required]="
                  isDeliveryMethodControlRequired ? 'true' : null
                "
                (change)="
                  onDeliveryMethodCheckboxChange(
                    'email',
                    $any($event.target).checked
                  )
                "
              />
              <label
                class="tw-mb-0 tw-cursor-pointer tw-select-none"
                for="alert-delivery-method-email"
              >
                Email
              </label>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <input
                class="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-neutral-400 focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-[3px]"
                type="checkbox"
                id="alert-delivery-method-text"
                [checked]="hasDeliveryMethod('text')"
                [attr.aria-required]="
                  isDeliveryMethodControlRequired ? 'true' : null
                "
                (change)="
                  onDeliveryMethodCheckboxChange(
                    'text',
                    $any($event.target).checked
                  )
                "
              />
              <label
                class="tw-mb-0 tw-cursor-pointer tw-select-none"
                for="alert-delivery-method-text"
              >
                Text
              </label>
            </div>
          </div>
          @if (isDeliveryMethodControlRequired) {
            <div aria-hidden="true" class="tw-px-2 tw-grow-0">*</div>
          }
        </div>
      </div>
    </div>
    @if (showEmail) {
      <div
        class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-pb-8 app-fade-in"
      >
        <app-page-header
          class="md:tw-col-span-2 tw-pt-8"
          label="Email settings"
          [level]="2"
          appearance="h3"
        />
        <div
          role="group"
          aria-labelledby="delivery-email-desc"
          class="tw-contents"
        >
          <span class="label" id="delivery-email-desc"> Email address </span>
          <span
            class="tw-pl-2 tw-pb-4 tw-break-words"
            [class.tw-italic]="!ownerEmailAddress"
            [class.tw-text-[var(--sed-black-60-color)]]="!ownerEmailAddress"
          >
            {{ ownerEmailAddress || 'blank' }}
          </span>
        </div>
        @if (!hasEmailDeliveryRecipient) {
          <div
            class="md:tw-col-span-2 tw-text-sm tw-text-[var(--sed-status-red-color)]"
            role="alert"
          >
            No email address is associated with your user account for alerts.
            Add at least one additional email recipient.
          </div>
        }
        <div
          role="group"
          aria-labelledby="delivery-addlemail-desc"
          class="tw-contents"
        >
          <span class="label" id="delivery-addlemail-desc">
            Additional email recipients
          </span>
          <div>
            @for (
              email of fg.controls.emails.controls;
              track email;
              let i = $index;
              let last = $last
            ) {
              <div class="tw-flex" [class.tw-pb-2]="!last">
                <app-input
                  #emailInput
                  [control]="$any(email)"
                  label="Email address"
                  class="tw-grow"
                />

                <app-icon-button
                  icon="fa-light fa-xmark"
                  (click)="removeEmail(i, $event)"
                  ariaLabel="Remove email recipient"
                />
                @if (last) {
                  <app-icon-button
                    #emailAddButton
                    icon="fa-light fa-add"
                    (click)="addEmail($event)"
                    ariaLabel="Add additional email recipient"
                  />
                }
              </div>
            } @empty {
              <div class="tw-flex tw-justify-end">
                <app-icon-button
                  #emailAddButton
                  icon="fa-light fa-add"
                  (click)="addEmail($event)"
                  ariaLabel="Add additional email recipient"
                />
              </div>
            }
          </div>
        </div>
        <div
          role="group"
          aria-labelledby="delivery-accum-desc"
          class="tw-contents"
        >
          <span class="label" id="delivery-accum-desc">
            Emails can be sent per alert or accumulated into batches. Would you
            like to receive accumulated emails?
          </span>
          <app-yes-no
            class="delivery-inline-radio"
            [control]="fg.controls.accumulatedEmail"
          />
        </div>

        <div
          role="group"
          aria-labelledby="delivery-excel-desc"
          class="tw-contents"
        >
          <span class="label" id="delivery-excel-desc">
            Include excel attachment in email?
          </span>
          <app-yes-no
            class="delivery-inline-radio"
            [control]="fg.controls.includeExcel"
          />
        </div>
      </div>
    }
    @if (showText) {
      <div
        class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-pb-8 app-fade-in"
      >
        <app-page-header
          class="md:tw-col-span-2 tw-pt-8"
          label="Text settings"
          [level]="2"
          appearance="h3"
        />

        <div
          role="group"
          aria-labelledby="delivery-textrecip-desc"
          class="tw-contents"
        >
          <span class="label" id="delivery-textrecip-desc">
            Add text recipients
          </span>
          <div>
            @for (
              phone of fg.controls.phones.controls;
              track phone;
              let i = $index;
              let last = $last
            ) {
              <div class="tw-flex" [class.tw-pb-2]="!last">
                <app-input
                  #phoneInput
                  [control]="$any(phone)"
                  label="Phone"
                  type="Phone"
                  class="tw-grow"
                />

                <app-icon-button
                  icon="fa-light fa-xmark"
                  (click)="removePhone(i, $event)"
                  ariaLabel="Remove text recipient"
                />
                @if (last) {
                  <app-icon-button
                    #phoneAddButton
                    icon="fa-light fa-add"
                    (click)="addPhone($event)"
                    ariaLabel="Add additional text recipient"
                  />
                }
              </div>
            } @empty {
              <div class="tw-flex tw-justify-end">
                <app-icon-button
                  #phoneAddButton
                  icon="fa-light fa-add"
                  (click)="addPhone($event)"
                  ariaLabel="Add additional text recipient"
                />
              </div>
            }
          </div>
        </div>

        <div
          role="group"
          aria-labelledby="delivery-textcount-desc"
          class="tw-contents"
        >
          <span class="label" id="delivery-textcount-desc">
            Maximum number of texts per day
          </span>
          <app-slider
            [control]="fg.controls.textCount"
            [min]="1"
            [max]="50"
            ariaLabelledBy="delivery-textcount-desc"
          />
        </div>
      </div>
    }

    <div class="tw-pt-8"></div>
    <div
      class="tw-flex tw-gap-2 tw-w-full tw-justify-center md:tw-justify-end tw-px-1.5 tw-pt-1 tw-pb-1.5"
    >
      <app-button
        class="tw-w-[44%] md:tw-w-auto"
        emphasis="Low"
        label="Previous"
        classes="tw-w-full md:tw-w-auto"
        (click)="previous()"
      />
      <app-button
        class="tw-w-[44%] md:tw-w-auto"
        label="Save"
        classes="tw-w-full md:tw-w-auto"
        [disabled]="!canSave"
        [loading]="saving"
        (click)="save()"
      />
    </div>
  `,
  styles: `
    .label {
      font-weight: bold;
      color: var(--sed-engaged-black-color);
      padding-top: 8px;
      text-wrap: balance;
      border-radius: 4px;
    }
    @media (max-width: 768px) {
      /* Bug 15: Global styles halve tw-gap-4 to 0.5rem on mobile, but tw-pt-8 section headers stay
         at 32px — making them 4× the row gap instead of desktop's 2×. Restore row-gap to 1rem so
         all section spacing is proportional and consistent with desktop rules. */
      div.tw-grid {
        row-gap: 1rem !important;
      }

      :host ::ng-deep .delivery-inline-radio legend {
        display: none;
      }

      :host ::ng-deep .delivery-inline-radio label {
        margin-top: 0 !important;
        line-height: 1.1;
      }

      :host ::ng-deep app-yes-no.delivery-inline-radio fieldset > div.tw-flex {
        display: grid !important;
        grid-template-columns: 1fr;
        column-gap: 0.75rem;
        row-gap: 0.25rem;
      }

      :host
        ::ng-deep
        app-yes-no.delivery-inline-radio
        fieldset
        > div.tw-flex
        > div.tw-flex {
        padding-right: 0 !important;
        min-width: 0;
        flex-wrap: nowrap !important;
      }

      :host ::ng-deep app-radio-button.delivery-inline-radio form.tw-flex {
        display: grid !important;
        grid-template-columns: 1fr;
        column-gap: 0.75rem;
        row-gap: 0.25rem;
      }

      :host
        ::ng-deep
        app-radio-button.delivery-inline-radio
        form.tw-flex
        > div.tw-flex {
        padding-right: 0 !important;
        min-width: 0;
        flex-wrap: nowrap !important;
      }
    }
  `,
})
export class AlertDeliveryStepComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private alerts = inject(AlertsService);
  private snackbar = inject(Snackbar);
  private el = inject(ElementRef);
  private injector = inject(Injector);

  @Input()
  stepper: StepperComponent;

  @ViewChildren('emailInput')
  private emailInputs!: QueryList<InputComponent>;

  @ViewChildren('emailAddButton', { read: ElementRef })
  private emailAddButtons!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('phoneInput')
  private phoneInputs!: QueryList<InputComponent>;

  @ViewChildren('phoneAddButton', { read: ElementRef })
  private phoneAddButtons!: QueryList<ElementRef<HTMLElement>>;

  fg = new FormGroup({
    title: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(64),
      Validators.pattern(/^[a-zA-Z0-9\-_/ ]+$/),
    ]),
    deliveryMethod: new FormControl<string[]>(null, Validators.required),
    deliveryFrequency: new FormControl<ListItem | string>(
      'daily',
      Validators.required,
    ),
    accumulatedEmail: new FormControl<boolean>(true),
    textMessages: new FormControl<boolean>(null),
    disableEmail: new FormControl<boolean>(null),
    includeExcel: new FormControl<boolean>(null),
    enabled: new FormControl<boolean>(true),
    textCount: new FormControl<number>(10),
    startDate: new FormControl<Date>(null, Validators.required),
    endDate: new FormControl<Date>(null, Validators.required),
    phones: new FormArray([]),
    emails: new FormArray([]),
  });

  deliveryFrequency: { description: string; value: string }[] = [];

  protected showText = false;

  protected showEmail = false;

  protected saving = false;

  protected ownerEmailAddress: string | null = null;

  protected get hasEmailDeliveryRecipient(): boolean {
    if (!this.hasDeliveryMethod('email')) {
      return true;
    }

    if (this.ownerEmailAddress?.trim()) {
      return true;
    }

    return this.fg.controls.emails.controls.some(
      (ctrl) => !!ctrl.value?.trim() && ctrl.valid,
    );
  }

  protected get canSave(): boolean {
    if (!this.fg.valid) {
      return false;
    }
    return this.hasEmailDeliveryRecipient;
  }

  protected get isDeliveryMethodControlRequired(): boolean {
    return this.fg.controls.deliveryMethod.hasValidator(Validators.required);
  }

  protected hasDeliveryMethod(value: string): boolean {
    const v = this.fg.controls.deliveryMethod.value;
    return Array.isArray(v) && v.includes(value);
  }

  protected onDeliveryMethodCheckboxChange(
    value: string,
    checked: boolean,
  ): void {
    const ctrl = this.fg.controls.deliveryMethod;
    const current = [...(ctrl.value ?? [])];
    if (checked) {
      if (!current.includes(value)) {
        current.push(value);
      }
    } else {
      const idx = current.indexOf(value);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }

    ctrl.setValue(current.length > 0 ? current : null);
    ctrl.markAsTouched();
    ctrl.updateValueAndValidity();
  }

  private lastToInputText = '';
  private toDateInput: HTMLInputElement | null = null;
  private boundToInputHandler = (e: Event) => {
    this.lastToInputText = (e.target as HTMLInputElement).value || '';
  };
  private boundToBlurHandler = () => {
    this.onToDateBlur();
  };

  private onToDateBlur(): void {
    if (!this.lastToInputText) {
      return;
    }

    const parsed = new Date(this.lastToInputText);
    const startDate = this.fg.controls.startDate.value;

    if (!isNaN(parsed.getTime()) && startDate instanceof Date) {
      const maxDate = new Date(startDate.getTime());
      maxDate.setDate(maxDate.getDate() + 365);
      maxDate.setHours(0, 0, 0, 0);

      if (parsed > maxDate) {
        setTimeout(() => {
          this.fg.controls.endDate.setErrors({
            required: true,
            maxDate: 'Maximum 365 days from the start date',
          });
          this.fg.controls.endDate.markAsTouched();
        }, 100);
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const rangeEl = this.el.nativeElement.querySelector('app-range');
      if (rangeEl) {
        const inputs = rangeEl.querySelectorAll('input');
        if (inputs.length >= 2) {
          this.toDateInput = inputs[1];
          this.toDateInput.addEventListener('input', this.boundToInputHandler);
          this.toDateInput.addEventListener('blur', this.boundToBlurHandler);
        }
      }
    });
  }

  override ngOnDestroy(): void {
    if (this.alerts.currentAlert()) {
      this.saveFormToAlert();
    }

    super.ngOnDestroy();
    if (this.toDateInput) {
      this.toDateInput.removeEventListener('input', this.boundToInputHandler);
      this.toDateInput.removeEventListener('blur', this.boundToBlurHandler);
    }
  }

  async ngOnInit() {
    this.ownerEmailAddress = this.session.user?.email || null;

    const alert = this.alerts.currentAlert();

    const isDailyOnly = alert?.conditionCard?.dailyOnly === true;
    this.deliveryFrequency = isDailyOnly
      ? [{ description: 'Daily', value: 'daily' }]
      : [
          { description: 'Daily', value: 'daily' },
          { description: 'Near real time', value: 'nrt' },
        ];

    if (isDailyOnly) {
      this.fg.controls.deliveryFrequency.setValue('daily');
    }

    this.dispose.subs = this.fg.controls.deliveryMethod.valueChanges.subscribe(
      (method: string[]) => {
        this.showText = method.some((x) => x === 'text');
        if (!this.showText) {
          this.fg.controls.phones.clear();
        } else {
          if (this.fg.controls.phones.length === 0) {
            this.addPhone();
          }
        }

        this.showEmail = method.some((x) => x === 'email');
        if (!this.showEmail) {
          this.fg.controls.emails.clear();
        } else {
          if (this.fg.controls.emails.length === 0) {
            this.addEmail();
          }
        }

        this.fg.controls.endDate.markAsTouched();
        this.fg.controls.endDate.updateValueAndValidity();
      },
    );

    if (alert) {
      const titleValue =
        alert.description || (alert as any).conditionCard?.title || null;
      if (titleValue) {
        this.fg.controls.title.setValue(titleValue);
      }
      if (alert.enabled != null) {
        this.fg.controls.enabled.setValue(alert.enabled);
      }
      const parsedStart = DateEx.parseDateOnly(alert.startDate);
      if (parsedStart) {
        this.fg.controls.startDate.setValue(parsedStart);
      } else {
        this.fg.controls.startDate.setValue(DateEx.clearTime(new Date()));
      }
      const parsedEnd = DateEx.parseDateOnly(alert.endDate);
      if (parsedEnd) {
        this.fg.controls.endDate.setValue(parsedEnd);
      }

      const methods: string[] = [];
      if (alert.sendEmail) methods.push('email');
      if (alert.sendText) methods.push('text');
      if (methods.length > 0) {
        this.fg.controls.deliveryMethod.setValue(methods);
        this.showEmail = methods.includes('email');
        this.showText = methods.includes('text');
      }

      if (alert.notificationFreq) {
        const freqStr = this.alerts.getNotificationFrequencyString(
          alert.notificationFreq,
        );
        this.fg.controls.deliveryFrequency.setValue(
          freqStr || ('daily' as any),
        );
      } else if (!this.fg.controls.deliveryFrequency.value) {
        this.fg.controls.deliveryFrequency.setValue('daily');
      }

      if (alert.aggregateEmail != null) {
        this.fg.controls.accumulatedEmail.setValue(alert.aggregateEmail);
      }
      if (alert.includeAttachment != null) {
        this.fg.controls.includeExcel.setValue(alert.includeAttachment);
      }

      if (this.showEmail) {
        if (alert.additionalEmails?.length > 0) {
          this.fg.controls.emails.clear();
          alert.additionalEmails.forEach((email) => {
            const ctrl = new FormControl(email.emailAddress, [
              Validators.required,
              Validators.email,
              this.duplicateEmailValidator(),
            ]);
            this.fg.controls.emails.push(ctrl);
            this.dispose.subs = ctrl.valueChanges.subscribe(() =>
              this.revalidateSiblingEmails(ctrl),
            );
          });
        } else if (this.fg.controls.emails.length === 0) {
          this.addEmail();
        }
      }

      if (this.showText) {
        if (alert.smsPhones?.length > 0) {
          this.fg.controls.phones.clear();
          alert.smsPhones.forEach((phone) => {
            this.fg.controls.phones.push(
              this.createPhoneControl(phone.phoneNumber),
            );
          });
        } else if (this.fg.controls.phones.length === 0) {
          this.addPhone();
        }
      }

      if (alert.maxTextsPerDay != null) {
        this.fg.controls.textCount.setValue(alert.maxTextsPerDay);
      }
    }
  }

  previous() {
    this.saveFormToAlert();
    this.stepper.previous();
  }

  async save() {
    if (this.alerts.isEditMode()) {
      this.confirm.show({
        label: 'Edit alert',
        message:
          'Editing this Alert will delete all historical alert messages. Do you wish to proceed?',
        width: '35rem',
        confirm: async () => {
          await this.performSave();
        },
      });
    } else {
      await this.performSave();
    }
  }

  private async performSave() {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    this.saveFormToAlert();

    try {
      await this.alerts.saveCurrentAlert();
      this.alerts.clearAlert();
      this.snackbar.show(
        'Your alert has been saved. All alert messages are retained for 30 days.',
        'Success',
      );
      this.router.navigate(['alerts'], {
        queryParams: {
          tab: 'my-alerts',
        },
      });
    } finally {
      this.saving = false;
    }
  }

  private saveFormToAlert() {
    const alert = this.alerts.currentAlert();

    // Map conditionCard to condition (for backend WCF model)
    const conditionCard = (alert as any).conditionCard;
    if (conditionCard) {
      const conditionValues = this.getConditionValues(
        alert,
        conditionCard.type,
      );
      const conditionTitle =
        conditionCard.description || conditionCard.title || '';

      // Build newValues first so we can use descriptions for display text
      const newValues = this.buildNewValues(
        alert,
        conditionValues,
        conditionCard.type,
      );

      const displayValue = this.getConditionDisplayValueFromNewValues(
        newValues,
        conditionValues.condOperator,
        conditionCard.type,
      );
      const conditionText = this.getConditionText(
        conditionCard.type,
        conditionTitle,
        conditionValues.condOperator,
        displayValue || conditionValues.value1,
        conditionValues.value2,
        alert,
      );

      const individuals: EmployeeValue[] = (alert as any).individuals ?? [];
      const employeeCollection: any[] = individuals.map((ind: any) => ({
        empUnum: ind.empUnum ?? null,
        employeeID: ind.employeeID ?? ind.employeeId ?? null,
        firstName: ind.firstName ?? null,
        lastName: ind.lastName ?? null,
        ssn: ind.ssn ?? null,
        ssN4: ind.ssn4 ?? (ind.ssn ? ind.ssn.toString().slice(-4) : null),
        dateHire: ind.dateHire ?? null,
        recSource: ind.recSource
          ? String(ind.recSource).trim().slice(0, 1)
          : null,
      }));

      alert.condition = {
        conditionType: conditionCard.type ?? 'Unknown',
        operatorType:
          (conditionValues.condOperator as OperatorType) ?? 'Unknown',
        text: conditionText,
        newValues: newValues,
        employeeCollection: employeeCollection,
      };
    }

    // alert.contracts already set in step 1 (condition step) with contNum + description
    // Map client to contracts
    const client = (alert as any).client;
    const clientDescription = (alert as any).clientDescription ?? '';
    if (client) {
      const clientNum = typeof client === 'string' ? client : client.value;
      alert.contracts = [
        {
          contNum: parseInt(clientNum, 10),
          description: clientDescription,
        },
      ];
    }

    if (this.user?.isAgentUser) {
      (alert as any).locationMethod = null;
      (alert as any).accounts = null;
      (alert as any).structures = null;
      // Agent users have no location selection UI. Send CAU so the Alerts Processor
      // (which does not accept LocationLookupType.Unknown) can validate the alert.
      alert.locationLookupType = 'CAU';
      alert.locationIncludeType = 'Include';
      alert.locationText = '';
      alert.locations = [];
    } else {
      const locationMethod = (alert as any).locationMethod;
      // default to Account and unit (CAU) on submit
      // when the user did not choose Select by. UI stays blank and optional.
      alert.locationLookupType =
        locationMethod === 'Structure' ? 'Structure' : 'CAU';
      alert.locations = [];

      if (alert.locationLookupType === 'CAU' && !locationMethod) {
        alert.locationIncludeType = 'Include';
        alert.locationText = '';
      }

      // Map structures to locations (corporate structure selection)
      const structures = (alert as any).structures;
      if (structures?.nodes?.length > 0) {
        alert.locationIncludeType = structures.includeType;
        alert.locations = structures.nodes.map((node: any) => ({
          corpStructNodeID: node.node || 0,
          locationName: node.name,
          accountNumber: this.parseNumber(node.accountNumber) ?? 0,
          unitNumber: node.unitNumber?.toString() ?? '',
          accountDescription: '',
          accountUnit: '',
          code: '',
          hasChildren: false,
          isUnit: '',
          parentNodeID: 0,
          unitDescription: '',
        }));
      }

      // Map accounts to locations (account/unit selection)
      // Note: accounts object has "accounts" array, not "items"
      const accountsData = (alert as any).accounts;
      const accountsList = accountsData?.accounts || accountsData?.items || [];
      if (accountsList.length > 0) {
        alert.locationIncludeType = accountsData.includeType;
        alert.locations = accountsList.map((acc: any) => ({
          corpStructNodeID: 0,
          locationName: acc.accountName || acc.name || '',
          accountNumber: this.parseNumber(acc.accountNumber) ?? 0,
          unitNumber: acc.unitNumber?.toString() ?? '',
          accountDescription: '',
          accountUnit: '',
          code: '',
          hasChildren: false,
          isUnit: '',
          parentNodeID: 0,
          unitDescription: '',
        }));
      }
    }

    // Map state selections
    this.mapStatesToAlert(alert);

    // Delivery form fields
    alert.description = this.fg.controls.title.value;
    alert.enabled = this.fg.controls.enabled.value ?? true;
    alert.startDate = DateEx.toUtcDateOnly(this.fg.controls.startDate.value);
    alert.endDate = DateEx.toUtcDateOnly(this.fg.controls.endDate.value);

    const methods = this.fg.controls.deliveryMethod.value || [];
    alert.sendEmail = methods.includes('email');
    alert.sendText = methods.includes('text');

    const freq = this.fg.controls.deliveryFrequency.value;
    const freqStr = typeof freq === 'object' ? (freq as any)?.value : freq;
    alert.notificationFreq =
      this.alerts.getNotificationFrequencyFromString(freqStr);

    alert.aggregateEmail = this.fg.controls.accumulatedEmail.value ?? false;
    alert.includeAttachment = this.fg.controls.includeExcel.value ?? false;

    alert.additionalEmails = this.fg.controls.emails.controls
      .map((ctrl) => ({
        emailAddress: ctrl.value,
        dateChanged: new Date(),
        emailSource: 2, // ContactSourceType.UserDefined
        emailSourceID: 0,
      }))
      .filter((e) => e.emailAddress) as any;

    alert.emailAddresses = alert.emailAddresses ?? [];

    alert.smsPhones = this.fg.controls.phones.controls
      .map((ctrl) => ({
        phoneNumber: ctrl.value,
        enabled: true,
        dateChanged: new Date(),
        phoneSource: 2, // ContactSourceType.UserDefined
        phoneSourceID: 0,
      }))
      .filter((p) => p.phoneNumber) as any;

    const rawMaxTexts = this.fg.controls.textCount.value ?? 10;
    alert.maxTextsPerDay = Math.min(50, Math.max(1, rawMaxTexts));

    // Update the signal with the mutated alert to ensure changes are persisted
    this.alerts.currentAlert.set({ ...alert });
  }

  private getConditionValues(
    alert: any,
    conditionType: string | undefined,
  ): {
    value1: string | null;
    value2: string | null;
    condOperator: string | null;
  } {
    const selectOp = alert.selectOp?.value || null;
    const amountOp = alert.amountOp?.value || null;

    switch (conditionType) {
      case 'PolicyDeductibleSirRange':
      case 'PolicyLimit': {
        const percentValue = this.getOptionValue(alert.percent);
        const financialValue = this.getOptionValue(alert.financial);
        const notifyDays = alert.notifyDays ?? '';
        const value1 = [
          percentValue ?? '',
          financialValue ?? '',
          notifyDays,
        ].join('|');
        return { value1, value2: null, condOperator: 'None' };
      }
      case 'LostTimeGuidelines': {
        const daysValue = this.getOptionValue(alert.days);
        return {
          value1: daysValue != null ? daysValue.toString() : null,
          value2: alert.notifyDays != null ? alert.notifyDays.toString() : null,
          condOperator: 'None',
        };
      }
      case 'CalendarRestrictedDays':
      case 'AggregateCalendarLostDays':
      case 'AggregateCalendarRestrictedDays':
      case 'CalendarLostDays':
        return {
          value1: alert.days != null ? alert.days.toString() : null,
          value2: alert.notifyDays != null ? alert.notifyDays.toString() : null,
          condOperator: 'None',
        };
      case 'TCMOpenReferralDays':
        return {
          value1: alert.days != null ? alert.days.toString() : null,
          value2: alert.notifyDays != null ? alert.notifyDays.toString() : null,
          condOperator: 'None',
        };
      case 'ClaimDateOpen':
        return {
          value1: alert.days != null ? alert.days.toString() : null,
          value2: null,
          condOperator: 'None',
        };
      case 'ExcessNotificationDate':
        return {
          value1: alert.days != null ? alert.days.toString() : null,
          value2: null,
          condOperator: 'None',
        };
      case 'LagTimeAlerts':
        return {
          value1: `${alert.lagFrom?.value || ''}|${alert.lagTo?.value || ''}|${
            alert.lagOp?.value || ''
          }|${alert.lagDays || ''}`,
          value2: null,
          condOperator: 'None',
        };
      case 'Litigation':
      case 'Subrogation': {
        const boolVal =
          typeof alert.selectBoolean === 'object'
            ? alert.selectBoolean?.value
            : alert.selectBoolean;
        return {
          value1: boolVal != null ? (boolVal ? 'true' : 'false') : null,
          value2: null,
          condOperator: 'Boolean',
        };
      }
      case 'ClaimantDeceased':
        // ClaimantDeceased uses OperatorType "Boolean" and Value "Y"
        return {
          value1: 'Y',
          value2: null,
          condOperator: 'Boolean',
        };
      case 'MultipleOpenClaims':
        // MultipleOpenClaims uses OperatorType "None" with no values
        return {
          value1: null,
          value2: null,
          condOperator: 'None',
        };
      case 'TotalIncurred':
      case 'ReserveChange':
      case 'PaymentAmount':
        return {
          value1: alert.amount != null ? alert.amount.toString() : null,
          value2: null,
          condOperator: amountOp,
        };
      case 'ClaimStatus':
      case 'ClaimType':
      case 'WorkStatus':
      case 'BenefitStatus':
        return {
          value1: this.getConditionValue1(alert),
          value2: null,
          condOperator: selectOp,
        };
      case 'CauseCode':
      case 'NatureResult':
      case 'BodyPart':
        return {
          value1: this.getConditionValue1(alert),
          value2: null,
          condOperator: selectOp || 'InList',
        };
      case 'PaymentPayCode':
        return {
          value1: this.getConditionValue1(alert),
          value2: null,
          condOperator: 'InList', // PaymentPayCode always uses InList
        };
      case 'ClientDefinedField': {
        const cfDt = (alert?.customField?.dataType || '').toLowerCase();
        return {
          value1: this.getConditionValue1(alert),
          value2: null,
          condOperator:
            cfDt === 'logical'
              ? 'None'
              : this.mapCustomFieldOperatorToWcf(alert?.customField?.operator),
        };
      }

      default:
        return {
          value1: this.getConditionValue1(alert),
          value2: null,
          condOperator: selectOp || 'None',
        };
    }
  }

  private getConditionValue1(alert: any): string | null {
    if (alert.selectPickListItems?.length > 0) {
      return alert.selectPickListItems.map((i: any) => i.value || i).join(',');
    }

    if (alert.selectItems?.length > 0) {
      return alert.selectItems.map((i: any) => i.value || i).join(',');
    }

    if (alert.selectBoolean != null) {
      const boolVal =
        typeof alert.selectBoolean === 'object'
          ? alert.selectBoolean.value
          : alert.selectBoolean;
      return boolVal ? 'true' : 'false';
    }

    if (alert.amount != null) {
      return alert.amount.toString();
    }

    if (alert.percent != null) {
      const percentValue = this.getOptionValue(alert.percent);
      return percentValue != null ? percentValue.toString() : null;
    }

    if (alert.lagFrom?.value) {
      return `${alert.lagFrom.value}|${alert.lagTo?.value || ''}|${
        alert.lagOp?.value || ''
      }|${alert.lagDays || ''}`;
    }

    if (alert.customField) {
      const values = [
        alert.customField.fieldNumber,
        alert.customField.operator,
        alert.customField.valueText,
        alert.customField.valueFrom,
        alert.customField.valueTo,
        alert.customField.days,
        alert.customField.notifyDays,
        alert.customField.booleanValue,
        (alert.customField.values || []).join('^'),
        alert.customField.dataType,
      ];
      return values
        .map((v: any) => (v != null && v !== '' ? String(v) : ''))
        .join('|');
    }

    return null;
  }

  private mapCustomFieldOperatorToWcf(op: string | null | undefined): string {
    if (!op) return 'None';
    const map: Record<string, string> = {
      inList: 'InList',
      notInList: 'NotInList',
      equalTo: 'EqualTo',
      equals: 'EqualTo',
      notEqualTo: 'NotEqualTo',
      notEquals: 'NotEqualTo',
      greaterThan: 'GreaterThan',
      greaterThanOrEqualTo: 'GreaterThanEqualTo',
      lessThan: 'LessThan',
      lessThanOrEqualTo: 'LessThanEqualTo',
      between: 'Between',
      like: 'Like',
      contains: 'Like',
      isNull: 'IsNull',
      isNotNull: 'IsNotNull',
    };
    return map[op] || 'None';
  }

  private getOptionValue(value: any): any {
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }

    return value;
  }

  private getConditionDisplayValueFromNewValues(
    newValues: { contNum: number; lob: string; name: string; value: string }[],
    operator: string | null,
    conditionType?: string,
  ): string | null {
    const isInListOperator = operator === 'InList' || operator === 'NotInList';
    if (!isInListOperator || newValues.length === 0) {
      return null;
    }

    return newValues
      .map((nv) => {
        if (!nv.value) {
          return null;
        }

        const name = this.stripNameLobSuffix(nv.name, nv.lob);
        const hasDescription =
          name && name.toLowerCase() !== nv.value.toLowerCase();
        if (!hasDescription) {
          return nv.value;
        }

        if (name.includes(' - ')) {
          return name;
        }

        return `${nv.value} - ${name}`;
      })
      .filter((v) => v)
      .join(', ');
  }

  private getConditionText(
    conditionType: string,
    title: string,
    operator: string | null,
    value1: string | null,
    value2: string | null,
    alert?: any,
  ): string {
    switch (conditionType) {
      case 'ClaimStatus':
        return this.classicListText('Claim status is', operator, value1);

      case 'ClaimType':
        return this.classicListText('The claim type is', operator, value1);

      case 'WorkStatus':
        return this.classicListText('Work status is', operator, value1);

      case 'BenefitStatus':
        return this.classicListText('The benefit status', operator, value1);

      case 'CauseCode':
        return this.classicListText('Cause is', operator, value1);

      case 'NatureResult':
        return this.classicListText('Nature/Result is', operator, value1);

      case 'BodyPart':
        return this.classicListText('Body Part/Target is', operator, value1);

      case 'PaymentPayCode':
        return this.classicEnsurePeriod(
          `The payment code is ${value1 || ''}`.trim(),
        );

      case 'TotalIncurred':
        return this.classicAmountText(
          'The total incurred amount',
          operator,
          value1,
        );

      case 'ReserveChange':
        return this.classicAmountText(
          'The total future reserve amount',
          operator,
          value1,
        );

      case 'PaymentAmount':
        return this.classicAmountText('Payment amount is', operator, value1);

      case 'ClaimDateOpen':
        return this.classicDaysText('Claim open date is within', value1);

      case 'ExcessNotificationDate':
        return this.classicDaysText(
          'The excess notification date is within',
          value1,
        );

      case 'CalendarLostDays':
        return this.classicDaysNotifyText(
          'Number of days off work in current period meets or exceeds',
          value1,
          value2,
        );

      case 'CalendarRestrictedDays':
        return this.classicDaysNotifyText(
          'Number of days since the last restriction meets or exceeds',
          value1,
          value2,
        );

      case 'AggregateCalendarLostDays':
        return this.classicDaysNotifyText(
          'The total calendar lost time days meets or exceeds',
          value1,
          value2,
        );

      case 'AggregateCalendarRestrictedDays':
        return this.classicDaysNotifyText(
          'Total restricted duty and restricted NTL days meets or exceeds',
          value1,
          value2,
        );

      case 'TCMOpenReferralDays':
        return this.classicDaysNotifyText(
          'TCM referral open days meet or exceed',
          value1,
          value2,
        );

      case 'LostTimeGuidelines':
        return this.classicLostTimeText(value1, value2);

      case 'Litigation':
        return `The claim litigation is ${this.classicFormatBoolean(value1)}`.trim();

      case 'Subrogation':
        return `The claim subrogation is ${this.classicFormatBoolean(value1)}`.trim();

      case 'AdjusterChange':
        return 'The Examiner has changed';

      case 'ClaimReopened':
        return 'The claim has been reopened';

      case 'ClaimantDeceased':
        return 'The claimant death flag is true';

      case 'MultipleOpenClaims':
        return 'Multiple open claims for claimant';

      case 'DefenseAttorneyChanged':
        return "The defense representing attorney's name has changed";

      case 'PlaintiffAttorney':
        return "The plaintiff representing attorney's name has changed";

      case 'PermanentTotalDisability':
        return 'Injury type is permanent total disability';

      case 'LegalCaseResolved':
        return 'The legal case has been resolved';

      case 'EventMultipleClaimants':
        return 'The event has multiple claimants';

      case 'IndividualHasClaimCase':
        return 'Has new claim or case';

      case 'ClientDefinedField':
        if (alert?.customField?.fieldName != null) {
          return this.buildCustomFieldConditionText(alert.customField);
        }
        return title;

      case 'LagTimeAlerts':
        return this.classicLagText(value1);

      case 'PolicyDeductibleSirRange':
        return this.classicPolicyText(false, value1);

      case 'PolicyLimit':
        return this.classicPolicyText(true, value1);

      default:
        return this.buildConditionText(title, operator, value1);
    }
  }

  private buildCustomFieldConditionText(cf: {
    fieldNumber?: number;
    fieldName?: string;
    dataType?: string;
    operator?: string;
    valueText?: string;
    values?: string[];
    valueFrom?: number;
    valueTo?: number;
    days?: number;
    notifyDays?: number;
    booleanValue?: boolean;
  }): string {
    const value1 = [
      cf.fieldNumber ?? '',
      cf.operator ?? '',
      cf.valueText ?? '',
      cf.valueFrom ?? '',
      cf.valueTo ?? '',
      cf.days ?? '',
      cf.notifyDays ?? '',
      cf.booleanValue != null ? cf.booleanValue : '',
      (cf.values || []).join('^'),
      cf.dataType ?? '',
    ]
      .map((v) => (v != null && v !== '' ? String(v) : ''))
      .join('|');
    return this.alerts.getCustomFieldConditionDisplayText(
      cf.fieldName ?? 'Custom field',
      value1,
    );
  }

  private buildConditionText(
    title: string,
    operator: string | null,
    value1: string | null,
  ): string {
    if (!operator && !value1) {
      return title;
    }

    const parts: string[] = [title];
    if (operator) {
      parts.push(this.getOperatorDisplayText(operator));
    }

    if (value1) {
      parts.push(value1);
    }

    return parts.join(' ');
  }

  private getOperatorDisplayText(operator: string): string {
    const map: Record<string, string> = {
      InList: 'In list',
      NotInList: 'Not in list',
      IncreasedBy: 'Increased by',
      DecreasedBy: 'Decreased by',
      GreaterThan: 'Greater than',
      GreaterThanEqualTo: 'Greater than or equal to',
      LessThan: 'Less than',
      LessThanEqualTo: 'Less than or equal to',
      EqualTo: 'Equal to',
      NotEqualTo: 'Not equal to',
      Between: 'Between',
      Boolean: 'is',
    };
    return map[operator] || operator;
  }

  private classicListText(
    prefix: string,
    operator: string | null,
    value: string | null,
  ): string {
    const opLabel = this.classicListOperator(operator);
    const parts = [prefix];

    if (opLabel) {
      parts.push(opLabel);
    }

    if (value) {
      parts.push(value);
    }

    return this.classicEnsurePeriod(parts.join(' ').trim());
  }

  private classicAmountText(
    prefix: string,
    operator: string | null,
    value: string | null,
  ): string {
    const opLabel = this.classicAmountOperator(operator);
    const amountLabel = this.classicFormatCurrency(value);
    return this.classicEnsurePeriod(
      `${prefix} ${opLabel || ''} ${amountLabel || ''}`.trim(),
    );
  }

  private classicDaysText(prefix: string, value: string | null): string {
    if (!value) {
      return this.classicEnsurePeriod(prefix.trim());
    }

    const days = parseInt(value, 10);
    const suffix =
      !Number.isNaN(days) && days === 1 ? '1 day' : `${value} days`;
    return this.classicEnsurePeriod(`${prefix} ${suffix}`.trim());
  }

  private classicDaysNotifyText(
    prefix: string,
    value: string | null,
    notifyDays: string | null,
  ): string {
    let text = this.classicEnsurePeriod(`${prefix} ${value || ''}`.trim());

    if (notifyDays && parseInt(notifyDays, 10) > 0) {
      const n = parseInt(notifyDays, 10);
      const label = n === 1 ? '1 day' : `${n} days`;
      text += `\n\nSend notifications for ${label}`;

      if (!text.endsWith('.')) {
        text += '.';
      }
    }

    return text;
  }

  private classicLostTimeText(
    rangeValue: string | null,
    notifyDays: string | null,
  ): string {
    const rangeMap: Record<string, string> = {
      '1': 'Mid-Range (50%)',
      '2': 'At-Risk (70%)',
      '3': 'At-Risk - 7 (70% - 7)',
    };
    const rangeLabel = rangeValue ? rangeMap[rangeValue] || rangeValue : '';
    let text = this.classicEnsurePeriod(
      `The lost time guidelines have reached ${rangeLabel}`.trim(),
    );

    if (notifyDays && parseInt(notifyDays, 10) > 0) {
      const n = parseInt(notifyDays, 10);
      const label = n === 1 ? '1 day' : `${n} days`;
      text += `\n\nSend notifications for ${label}`;

      if (!text.endsWith('.')) {
        text += '.';
      }
    }

    return text;
  }

  private classicLagText(value1: string | null): string {
    if (!value1) {
      return 'Lag time condition';
    }

    const parts = value1.split('|');

    if (parts.length < 4) {
      return 'Lag time condition';
    }

    const dateMap: Record<string, string> = {
      loss: 'Date of Loss',
      'reported-to': 'Date Reported to Client',
      'reported-by': 'Date Reported by Client',
      'reported-sedgwick': 'Date Reported to Sedgwick',
      created: 'Date Claim Created',
      opened: 'Date Claim Opened',
      reopened: 'Date Claim Reopened',
      closed: 'Date Claim Closed',
    };
    const opMap: Record<string, string> = {
      gte: 'greater than or equal to',
      gt: 'greater than',
      lte: 'less than or equal to',
      lt: 'less than',
      eq: 'equal to',
    };
    const fromLabel = dateMap[parts[0]] || parts[0];
    const toLabel = dateMap[parts[1]] || parts[1];
    const opLabel = opMap[parts[2]] || parts[2];
    const daysNum = parseInt(parts[3], 10);
    const daysLabel =
      !Number.isNaN(daysNum) && daysNum === 1 ? '1 day' : `${parts[3]} days`;
    return this.classicEnsurePeriod(
      `The lag time between ${fromLabel} and ${toLabel} is ${opLabel} ${daysLabel}`.trim(),
    );
  }

  private classicPolicyText(isEvent: boolean, value1: string | null): string {
    if (!value1) {
      return isEvent ? 'Policy limit condition' : 'Deductible/SIR condition';
    }

    const parts = value1.split('|');
    const percent = parts[0] ? `${parts[0]}%` : '';
    const financialMap: Record<string, string> = {
      incurred: 'Incurred',
      paid: 'Paid',
    };
    const financial = parts[1]
      ? financialMap[parts[1].toLowerCase()] || parts[1]
      : '';
    const financialText = financial ? ` ${financial}` : '';
    const notifyDays = parts[2] || '';

    let text = isEvent
      ? `The event has reached percentage of the policy limit ${percent}${financialText}`.trim()
      : `The claim/event amount has reached percentage of the deductible/SIR amount ${percent}${financialText}`.trim();

    if (notifyDays && parseInt(notifyDays, 10) > 0) {
      const n = parseInt(notifyDays, 10);
      const label = n === 1 ? '1 day' : `${n} days`;
      text += `\n\nSend notifications for ${label}`;
    }

    return text;
  }

  private classicListOperator(operator: string | null): string {
    if (!operator) {
      return '';
    }

    const map: Record<string, string> = {
      InList: 'in list',
      NotInList: 'not in list',
    };
    return map[operator] || operator;
  }

  private classicAmountOperator(operator: string | null): string {
    if (!operator) {
      return '';
    }

    const map: Record<string, string> = {
      GreaterThan: 'greater than',
      GreaterThanEqualTo: 'greater than or equal to',
      LessThan: 'less than',
      LessThanEqualTo: 'less than or equal to',
      EqualTo: 'equal to',
      IncreasedBy: 'increased by',
      DecreasedBy: 'decreased by',
    };
    return map[operator] || operator;
  }

  private classicFormatCurrency(value: string | null): string | null {
    if (!value) {
      return null;
    }

    const num = parseFloat(value.replace(/[$,]/g, ''));

    if (isNaN(num)) {
      return value;
    }

    const formatted = Math.abs(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return num < 0 ? `$-${formatted}` : `$${formatted}`;
  }

  private classicFormatBoolean(value: string | null): string {
    if (!value) {
      return '';
    }

    const lower = value.toLowerCase();

    if (['true', 'y', 'yes', '1'].includes(lower)) {
      return 'True';
    }

    if (['false', 'n', 'no', '0'].includes(lower)) {
      return 'False';
    }

    return value;
  }

  private classicEnsurePeriod(text: string): string {
    if (!text) {
      return '';
    }

    return text.endsWith('.') ? text : `${text}.`;
  }

  private parseNumber(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(num) ? null : num;
  }

  private mapStatesToAlert(alert: any): void {
    // States are now mapped to WCF format (statesOf* arrays) directly in the options step.
    // This method is kept for safety -- it's a no-op when the WCF arrays are already populated.
    if (
      !alert.statesOfJurisdiction?.length &&
      Array.isArray(alert.jurisdictionState) &&
      alert.jurisdictionState.length > 0
    ) {
      alert.statesOfJurisdiction = this.mapStateArray(alert.jurisdictionState);
    }

    if (
      !alert.statesOfLoss?.length &&
      Array.isArray(alert.lossState) &&
      alert.lossState.length > 0
    ) {
      alert.statesOfLoss = this.mapStateArray(alert.lossState);
    }

    if (
      !alert.statesOfPayroll?.length &&
      Array.isArray(alert.payrollState) &&
      alert.payrollState.length > 0
    ) {
      alert.statesOfPayroll = this.mapStateArray(alert.payrollState);
    }

    if (
      !alert.statesOfPremium?.length &&
      Array.isArray(alert.premiumState) &&
      alert.premiumState.length > 0
    ) {
      alert.statesOfPremium = this.mapStateArray(alert.premiumState);
    }
  }

  private mapStateArray(states: any[]): { code: string; name: string }[] {
    return states.map((s: any) => ({
      code: typeof s === 'string' ? s : s.value || s.code,
      name: typeof s === 'string' ? s : s.description || s.name,
    }));
  }

  private stripNameLobSuffix(
    name: string | undefined,
    lob: string | undefined,
  ): string {
    const n = name?.trimEnd() ?? '';
    const l = lob?.trim();
    if (!n || !l) {
      return n;
    }

    const suffix = ` (${l})`;
    if (n.toLowerCase().endsWith(suffix.toLowerCase())) {
      return n.slice(0, -suffix.length).trimEnd();
    }

    return n;
  }

  private findLoadedConditionCodeListItem(
    itemValue: string,
  ): ListItem | null | undefined {
    const items = this.alerts.loadedConditionCodes;
    if (!items?.length) {
      return undefined;
    }

    const exact = items.find((i) => i.value === itemValue);
    if (exact) {
      return exact;
    }

    const realValue = itemValue.includes(' - ')
      ? itemValue.split(' - ')[0].trim()
      : itemValue.split('_')[0];

    if (realValue !== itemValue) {
      return null;
    }

    return items.find((i) => i.value === realValue);
  }

  private resolveInListItemMeta(
    conditionType: string | undefined,
    itemValue: string,
    item: unknown,
  ): { contNum: number; lob: string } {
    let contNum = 0;
    let lob = '';
    if (item && typeof item === 'object') {
      const o = item as {
        lineOfBusiness?: string;
        contNum?: number;
      };
      if (o.lineOfBusiness != null && String(o.lineOfBusiness).trim()) {
        lob = String(o.lineOfBusiness).trim();
      }
      if (o.contNum != null && Number(o.contNum) > 0) {
        contNum = Number(o.contNum);
      }
    }

    if (
      (lob === '' || contNum === 0) &&
      ['CauseCode', 'NatureResult', 'BodyPart'].includes(conditionType || '')
    ) {
      const found = this.findLoadedConditionCodeListItem(itemValue);
      if (found) {
        if (!lob && found.lineOfBusiness) {
          lob = found.lineOfBusiness.trim();
        }
        if (contNum === 0 && found.contNum != null && found.contNum > 0) {
          contNum = found.contNum;
        }
      }
    }

    return { contNum, lob };
  }

  /**
   * Builds the newValues array for the condition.
   * For InList/NotInList operators, creates separate NameValue entries for each value.
   * For other operators, uses the traditional value1/value2 format.
   */
  private buildNewValues(
    alert: any,
    conditionValues: {
      value1: string | null;
      value2: string | null;
      condOperator: string | null;
    },
    conditionType?: string,
  ): { contNum: number; lob: string; name: string; value: string }[] {
    if (conditionType === 'ClientDefinedField' && alert?.customField) {
      return this.buildClientDefinedFieldNewValues(alert.customField);
    }

    if (conditionType === 'PermanentTotalDisability') {
      return [{ contNum: 0, lob: '', name: 'INJURY_TYPE', value: '2' }];
    }

    const newValues: {
      contNum: number;
      lob: string;
      name: string;
      value: string;
    }[] = [];
    const operator = conditionValues.condOperator;

    // Check if this is an InList/NotInList operator that should use array format
    const isInListOperator = operator === 'InList' || operator === 'NotInList';

    // BenefitStatus, ClaimantDeceased, and ExcessNotificationDate use empty name (legacy behavior)
    const useNullName =
      conditionType === 'BenefitStatus' ||
      conditionType === 'ClaimantDeceased' ||
      conditionType === 'ExcessNotificationDate';

    // Calendar day conditions use "daysBefore" and "daysToNotify" as names
    const useCalendarDayNames = [
      'AggregateCalendarLostDays',
      'AggregateCalendarRestrictedDays',
      'CalendarLostDays',
      'CalendarRestrictedDays',
      'TCMOpenReferralDays',
    ].includes(conditionType || '');

    if (isInListOperator) {
      const items = alert.selectItems || alert.selectPickListItems || [];
      items.forEach((item: any) => {
        const itemValue = typeof item === 'string' ? item : item.value || item;
        const itemName = useNullName
          ? ''
          : this.resolveDescription(conditionType, item);
        const realValue = itemValue.includes(' - ')
          ? itemValue.split(' - ')[0]
          : itemValue.split('_')[0];
        if (realValue) {
          const { contNum, lob } = this.resolveInListItemMeta(
            conditionType,
            itemValue,
            item,
          );
          newValues.push({
            contNum,
            lob,
            name: itemName,
            value: realValue,
          });
        }
      });
    } else {
      // For other operators, use the traditional value1/value2 format
      if (conditionValues.value1) {
        const entryName =
          conditionType === 'ClientDefinedField'
            ? alert?.customField?.fieldName?.trim() &&
              !/^Value\s*1$/i.test(alert.customField.fieldName.trim())
              ? String(alert.customField.fieldName.trim())
              : 'Custom field'
            : useNullName
              ? ''
              : useCalendarDayNames
                ? 'daysBefore'
                : 'Value1';
        newValues.push({
          contNum: 0,
          lob: '',
          name: entryName,
          value: conditionValues.value1,
        });
      }
      if (conditionValues.value2) {
        newValues.push({
          contNum: 0,
          lob: '',
          name: useNullName
            ? ''
            : useCalendarDayNames
              ? 'daysToNotify'
              : 'Value2',
          value: conditionValues.value2,
        });
      }
    }

    return newValues;
  }

  /**
   * Builds processor-compatible named NameValue entries for ClientDefinedField.
   * Order must match ViaOneView / what the Alerts Processor expects by index:
   * [0]=FieldNum, [1]=FieldName, [2]=DataType, [3]=MCFValue,
   * [4]=NotifyDays, [5]=IsLookup, [6]=FieldFormat.
   * The processor also looks up FieldNum, DataType, MCFValue, IsLookup by name.
   */
  private buildClientDefinedFieldNewValues(
    cf: any,
  ): { contNum: number; lob: string; name: string; value: string }[] {
    const entry = (name: string, value: string) => ({
      contNum: 0,
      lob: '',
      name,
      value,
    });

    const fieldNum = String(cf.fieldNumber ?? 0);
    const fieldName =
      cf.fieldName != null && String(cf.fieldName).trim() !== ''
        ? String(cf.fieldName).trim()
        : '';
    const dataType = cf.dataType || '';
    const hasLookupValues = Array.isArray(cf.values) && cf.values.length > 0;
    const isLookup = hasLookupValues ? 'true' : 'false';

    let mcfValue = '';
    if (hasLookupValues) {
      mcfValue = cf.values.join(',');
    } else if (cf.valueText != null && cf.valueText !== '') {
      mcfValue = cf.valueText;
    } else if (cf.valueFrom != null) {
      mcfValue =
        cf.valueTo != null
          ? `${cf.valueFrom},${cf.valueTo}`
          : String(cf.valueFrom);
    } else if (cf.booleanValue != null) {
      mcfValue = cf.booleanValue ? 'Y' : 'N';
    } else if (cf.days != null) {
      mcfValue = String(cf.days);
    }

    const notifyDays =
      cf.notifyDays != null && String(cf.notifyDays) !== ''
        ? String(cf.notifyDays)
        : '1';

    return [
      entry('FieldNum', fieldNum),
      entry('FieldName', fieldName),
      entry('DataType', dataType),
      entry('MCFValue', mcfValue),
      entry('NotifyDays', notifyDays),
      entry('IsLookup', isLookup),
      entry('FieldFormat', ''),
    ];
  }

  private resolveDescription(
    conditionType: string | undefined,
    codeOrItem: string | any,
  ): string {
    const code =
      typeof codeOrItem === 'string'
        ? codeOrItem
        : (codeOrItem?.value ?? codeOrItem);

    if (code.includes(' - ')) {
      return code;
    }

    const realCode = code.split('_')[0];

    let items: ListItem[];
    switch (conditionType) {
      case 'ClaimStatus':
        items = this.alerts.claimStatusOptions;
        break;
      case 'ClaimType':
        items = this.alerts.claimTypes;
        break;
      case 'WorkStatus':
        items = this.alerts.workStatus;
        break;
      case 'CauseCode':
      case 'NatureResult':
      case 'BodyPart':
        items = this.alerts.loadedConditionCodes;
        break;
      case 'PaymentPayCode': {
        if (typeof codeOrItem !== 'string' && codeOrItem?.label) {
          return this.extractPayCodeDescription(
            String(codeOrItem.label),
            String(realCode),
          );
        }

        for (const group of this.alerts.payCodeTypes) {
          const found = group.items?.find(
            (i: any) => String(i.value) === String(realCode),
          );
          if (found?.label) {
            return this.extractPayCodeDescription(
              String(found.label),
              String(realCode),
            );
          }
        }

        return code;
      }
      default:
        return code;
    }

    const exact = items?.find((i) => i.value === code);
    if (exact?.description) {
      return exact.description;
    }

    if (code === realCode) {
      const base = items?.find((i) => i.value === realCode);
      if (base?.description) {
        return base.description;
      }
    }

    return code;
  }

  private extractPayCodeDescription(label: string, code: string): string {
    const dashPrefix = code + ' - ';
    if (label.startsWith(dashPrefix)) {
      return label.substring(dashPrefix.length);
    }

    const spacePrefix = code + ' ';
    if (label.startsWith(spacePrefix)) {
      return label.substring(spacePrefix.length);
    }

    return label;
  }

  addPhone(event?: Event) {
    const focusNew = shouldScheduleFocusAfterAction(event);
    this.fg.controls.phones.push(this.createPhoneControl());
    if (focusNew) {
      scheduleFocusAtIndex(
        this.phoneInputs,
        this.fg.controls.phones.length - 1,
        this.injector,
      );
    }
  }

  private createPhoneControl(value = ''): FormControl {
    const ctrl = new FormControl(value, Validators.required);
    ctrl.addValidators(
      ValidatorsEx.phone(ctrl, 'Phone number should be 10 digits'),
    );
    return ctrl;
  }

  removePhone(i: number, event?: Event) {
    const focusAddButton = shouldScheduleFocusAfterAction(event);
    this.fg.controls.phones.removeAt(i);
    if (focusAddButton) {
      scheduleFocusLast(this.phoneAddButtons, this.injector, {
        descendantSelector: 'button',
      });
    }
  }

  addEmail(event?: Event) {
    const focusNew = shouldScheduleFocusAfterAction(event);
    const ctrl = new FormControl(null, [
      Validators.email,
      this.duplicateEmailValidator(),
    ]);
    this.fg.controls.emails.push(ctrl);
    this.dispose.subs = ctrl.valueChanges.subscribe(() =>
      this.revalidateSiblingEmails(ctrl),
    );
    if (focusNew) {
      scheduleFocusAtIndex(
        this.emailInputs,
        this.fg.controls.emails.length - 1,
        this.injector,
      );
    }
  }

  removeEmail(i: number, event?: Event) {
    const focusAddButton = shouldScheduleFocusAfterAction(event);
    if (this.fg.controls.emails.length === 1) {
      this.fg.controls.emails.at(0).reset();
    } else {
      this.fg.controls.emails.removeAt(i);
    }
    this.revalidateAllEmails();
    if (focusAddButton) {
      scheduleFocusLast(this.emailAddButtons, this.injector, {
        descendantSelector: 'button',
      });
    }
  }

  private duplicateEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const email = control.value?.trim()?.toLowerCase();
      if (!email) {
        return null;
      }

      if (email === this.ownerEmailAddress?.toLowerCase()) {
        return {
          duplicate: "This email address already exists in 'Email Recipients'",
        };
      }

      const siblings = this.fg.controls.emails.controls;
      for (const sibling of siblings) {
        if (sibling === control) {
          continue;
        }

        if (sibling.value?.trim()?.toLowerCase() === email) {
          return {
            duplicate:
              "This email address already exists in 'Email Recipients'",
          };
        }
      }

      return null;
    };
  }

  private revalidateSiblingEmails(changed: AbstractControl): void {
    for (const ctrl of this.fg.controls.emails.controls) {
      if (ctrl !== changed) {
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  private revalidateAllEmails(): void {
    for (const ctrl of this.fg.controls.emails.controls) {
      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }
}
